import { useRef } from "react";
import { motion, useScroll, useTransform, useMotionValue, useSpring } from "framer-motion";
import { EASE, MaskedLine } from "@/components/motion";
import { Sparkle, Globe, CursorArrow, ArrowDiag } from "@/components/Icons";
import { Float, Smiley } from "@/components/Mascots";

const LETTERS = "FLYTAB".split("");

export default function Hero() {
  const ref = useRef(null);
  const mx = useMotionValue(0);
  const my = useMotionValue(0);
  const sx = useSpring(mx, { stiffness: 45, damping: 18 });
  const sy = useSpring(my, { stiffness: 45, damping: 18 });
  const globeX = useTransform(sx, (v) => v * 42);
  const globeY = useTransform(sy, (v) => v * 30);
  const cursorX = useTransform(sx, (v) => v * -60);
  const cursorY = useTransform(sy, (v) => v * -42);

  const { scrollYProgress } = useScroll({ target: ref, offset: ["start start", "end start"] });
  const wordY = useTransform(scrollYProgress, [0, 1], [0, 220]);
  const wordScale = useTransform(scrollYProgress, [0, 1], [1, 0.92]);

  const onMouseMove = (e) => {
    const r = ref.current?.getBoundingClientRect();
    if (!r) return;
    mx.set((e.clientX - r.left) / r.width - 0.5);
    my.set((e.clientY - r.top) / r.height - 0.5);
  };

  return (
    <section
      id="top"
      ref={ref}
      onMouseMove={onMouseMove}
      className="on-green relative min-h-screen bg-flytab-green text-flytab-black overflow-hidden flex flex-col"
      data-testid="hero-section"
    >
      <motion.div
        className="fixed inset-0 z-[90] bg-flytab-black flex items-center justify-center pointer-events-none"
        initial={{ y: 0 }}
        animate={{ y: "-100%" }}
        transition={{ duration: 1.05, ease: [0.76, 0, 0.24, 1], delay: 0.35 }}
        data-testid="hero-intro-overlay"
      >
        <span className="font-mono text-flytab-green text-xs md:text-sm uppercase tracking-[0.3em]">
          FLYTAB — Loading the bold
        </span>
      </motion.div>

      <motion.div style={{ x: globeX, y: globeY }} className="absolute right-[5%] top-[13%] hidden md:block z-10">
        <Globe className="w-28 lg:w-40 xl:w-48" />
      </motion.div>
      <motion.div style={{ x: cursorX, y: cursorY }} className="absolute right-[19%] top-[27%] hidden md:block z-10">
        <CursorArrow className="w-14 lg:w-20 rotate-[18deg]" />
      </motion.div>

      <motion.div
        initial={{ scale: 0, rotate: -25 }}
        animate={{ scale: 1, rotate: 0 }}
        transition={{ delay: 1.9, type: "spring", stiffness: 180, damping: 14 }}
        className="absolute left-[30%] top-[15%] hidden md:block z-10"
        data-testid="hero-mascot-smiley"
      >
        <Float amp={10} dur={6}>
          <Smiley className="w-14 lg:w-16 rotate-12" wink />
        </Float>
      </motion.div>

      <div className="flex-1 flex flex-col justify-end px-4 md:px-10 pt-28 md:pt-32 pb-6 md:pb-10 relative z-20">
        <div className="flex flex-wrap items-end justify-between gap-4 mb-2 md:mb-0">
          <MaskedLine delay={0.55}>
            <span className="font-mono text-[11px] md:text-sm uppercase tracking-[0.3em] font-medium">
              <span className="sm:hidden">Studio digital ✦</span>
              <span className="hidden sm:inline">Studio digital ✦ Sites — Apps — Web apps</span>
            </span>
          </MaskedLine>
          <MaskedLine delay={0.65} className="hidden sm:block">
            <span className="font-mono text-[11px] md:text-sm uppercase tracking-[0.3em] font-medium">
              (Paris → Worldwide)
            </span>
          </MaskedLine>
        </div>

        <motion.div style={{ y: wordY, scale: wordScale }} className="relative origin-bottom-left">
          <motion.div
            className="absolute -top-5 md:-top-7 left-[0.5%] z-10"
            animate={{ y: [0, -16, 0], rotate: [6, 16, 6] }}
            transition={{ duration: 5, repeat: Infinity, ease: "easeInOut" }}
          >
            <Sparkle className="w-11 md:w-16 lg:w-20" />
          </motion.div>

          <h1
            className="font-display font-black uppercase leading-[0.85] tracking-[-0.03em] select-none text-[clamp(4.2rem,16.5vw,21rem)]"
            data-testid="hero-wordmark"
          >
            <span className="sr-only">FLYTAB — Agence de création de sites web, applications et web apps</span>
            <span aria-hidden="true">
              {LETTERS.map((letter, i) => (
                <span key={i} className="inline-block overflow-hidden align-bottom pb-[0.06em] -mb-[0.06em]">
                  <motion.span
                    className="inline-block will-change-transform"
                    initial={{ y: "118%", rotate: 5 }}
                    animate={{ y: "0%", rotate: 0 }}
                    transition={{ duration: 1, ease: EASE, delay: 0.7 + i * 0.075 }}
                  >
                    {letter}
                  </motion.span>
                </span>
              ))}
            </span>
          </h1>
        </motion.div>

        <div className="mt-6 md:mt-10 grid grid-cols-1 md:grid-cols-12 gap-6 items-end">
          <div className="md:col-span-7">
            <MaskedLine delay={1.35}>
              <span className="font-display font-extrabold uppercase text-2xl md:text-4xl lg:text-[2.6rem] leading-[1.05] tracking-tight">
                Des sites web, des apps,
              </span>
            </MaskedLine>
            <MaskedLine delay={1.45}>
              <span className="font-display font-extrabold uppercase text-2xl md:text-4xl lg:text-[2.6rem] leading-[1.05] tracking-tight">
                des expériences qui claquent.
              </span>
            </MaskedLine>
          </div>

          <motion.div
            className="md:col-span-5 flex flex-wrap md:justify-end gap-3"
            initial={{ opacity: 0, y: 24 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.8, ease: EASE, delay: 1.7 }}
          >
            <button
              onClick={() => window.__lenis?.scrollTo("#contact", { duration: 1.5 })}
              className="rounded-full bg-flytab-black text-flytab-green font-display font-bold uppercase text-sm md:text-base px-8 py-4 transition-transform duration-200 hover:-translate-y-1 hover:shadow-[0_8px_0_rgba(0,0,0,0.35)] inline-flex items-center gap-2"
              data-testid="hero-cta-button"
            >
              Démarrer un projet <ArrowDiag className="w-4 h-4" />
            </button>
            <button
              onClick={() => window.__lenis?.scrollTo("#services", { duration: 1.5 })}
              className="rounded-full border-2 border-flytab-black text-flytab-black font-display font-bold uppercase text-sm md:text-base px-8 py-4 transition-colors duration-200 hover:bg-flytab-black hover:text-flytab-green"
              data-testid="hero-services-button"
            >
              Nos services
            </button>
            <p className="w-full md:text-right font-mono text-[11px] md:text-xs uppercase tracking-[0.2em] opacity-70" data-testid="hero-price-hint">
              Devis gratuit ✦ Réponse sous 24h
            </p>
          </motion.div>
        </div>
      </div>

      <motion.div
        className="relative z-20 border-t-2 border-flytab-black px-4 md:px-10 py-4 flex items-center justify-between font-mono text-[10px] md:text-xs uppercase tracking-[0.25em]"
        initial={{ opacity: 0 }}
        animate={{ opacity: 1 }}
        transition={{ delay: 2, duration: 0.8 }}
        data-testid="hero-bottom-bar"
      >
        <span className="pl-28 md:pl-40">©2026 FLYTAB</span>
        <motion.span animate={{ y: [0, 6, 0] }} transition={{ duration: 1.6, repeat: Infinity }} data-testid="hero-scroll-hint">
          Scroll ↓
        </motion.span>
        <span className="hidden md:inline">Made with bold</span>
      </motion.div>
    </section>
  );
}
